import { yup } from '@missionlabs/react'
import { HuntGroup, UserCallRoute } from '@missionlabs/types'
import { notAvailableSchema } from 'shared/schemas/notAvailable'

export type AddEditTeamFields = Pick<
    HuntGroup,
    | 'name'
    | 'IVR'
    | 'waitMusic'
    | 'customWaitMusicURL'
    | 'holdMusic'
    | 'customHoldMusicURL'
    | 'callRoute'
    | 'callTimeout'
    | 'notAvailable'
    | 'blockAnonymousCalls'
    | 'voicemail'
    | 'unansweredRedirect'
    | 'unansweredRedirectInternal'
    | 'busyRedirect'
    | 'busyRedirectInternal'
    | 'busyVoicemail'
    | 'outOfHoursRedirectInternal'
    | 'outOfHoursVoicemail'
    | 'phoneNumbers'
    | 'voicemailPin'
    | 'voicemailPinSet'
    | 'extension'
> &
    Pick<UserCallRoute, 'outOfHoursRedirect'>

export const addEditTeamDefaultValues: AddEditTeamFields = {
    name: '',
    IVR: { greeting: undefined, greetingEnabled: false },
    waitMusic: false,
    customWaitMusicURL: undefined,
    holdMusic: false,
    customHoldMusicURL: undefined,
    callRoute: {
        callSequence: 'group',
        teamMembers: [],
        inHoursUnanswered: 'ring_out',
        outOfHours: 'voicemail',
        busy: 'inactive'
    },
    notAvailable: { enabled: false },
    blockAnonymousCalls: false,
    callTimeout: undefined,
    unansweredRedirect: undefined,
    unansweredRedirectInternal: undefined,
    busyRedirect: undefined,
    busyRedirectInternal: undefined,
    busyVoicemail: {
        greeting: 'default',
        customGreetingURL: '',
        greetingNoMessage: 'default',
        customGreetingNoMessageURL: '',
        transcribe: true
    },
    voicemail: {
        greeting: 'default',
        customGreetingURL: '',
        greetingNoMessage: 'default',
        customGreetingNoMessageURL: '',
        transcribe: true
    },
    outOfHoursVoicemail: {
        greeting: 'default',
        customGreetingURL: '',
        greetingNoMessage: 'default',
        customGreetingNoMessageURL: '',
        transcribe: true
    },
    outOfHoursRedirectInternal: undefined,
    outOfHoursRedirect: undefined,
    phoneNumbers: [],
    extension: '',
    voicemailPin: undefined,
    voicemailPinSet: false
}

interface AddEditTeamSchemaArgs {
    voicemailPinRequired: boolean
}

export const addEditTeamSchema = ({ voicemailPinRequired = false }: AddEditTeamSchemaArgs) => {
    const voicemailPinSchema = yup.string().matches(/^\d{6}$/, {
        excludeEmptyString: true,
        message: 'Voicemail Pin must be exactly 6 digits'
    })

    return yup.object({
        name: yup.string().required(),
        busyVoicemail: yup.object({
            greeting: yup.string(),
            customGreetingURL: yup.string().when('greeting', {
                is: v => v === 'custom',
                then: () => yup.string().required()
            }),
            greetingNoMessage: yup.string().nullable(),
            customGreetingNoMessageURL: yup.string().when('greetingNoMessage', {
                is: v => v === 'custom',
                then: () => yup.string().required()
            })
        }),
        IVR: yup.object({
            greeting: yup.string().when('greetingEnabled', {
                is: v => v === true,
                then: () => yup.string().required()
            }),
            greetingEnabled: yup.boolean()
        }),
        waitMusic: yup.boolean(),
        customWaitMusicURL: yup.string().when('waitMusic', {
            is: v => v === true,
            then: () => yup.string()
        }),
        holdMusic: yup.boolean(),
        customHoldMusicURL: yup.string().when('holdMusic', {
            is: v => v === true,
            then: () => yup.string()
        }),
        callRoute: yup.object({
            teamMembers: yup.array().required(),
            inHoursUnanswered: yup.string().required(),
            outOfHours: yup.string()
        }),
        notAvailable: notAvailableSchema().default(undefined),
        blockAnonymousCalls: yup.boolean(),
        callTimeout: yup.number().when('callRoute.inHoursUnanswered', {
            is: v => v !== 'ring_out',
            then: () => yup.number().required()
        }),
        voicemail: yup.object({
            greeting: yup.string(),
            customGreetingURL: yup.string().when('greeting', {
                is: v => v === 'custom',
                then: () => yup.string().required()
            }),
            greetingNoMessage: yup.string().nullable(),
            customGreetingNoMessageURL: yup.string().when('greetingNoMessage', {
                is: v => v === 'custom',
                then: () => yup.string().required()
            })
        }),
        unansweredRedirect: yup.object().when('callRoute.inHoursUnanswered', {
            is: v => v === 'redirect',
            then: () => yup.object({ numberE164: yup.string().required() })
        }),
        unansweredRedirectInternal: yup.object().when('callRoute.inHoursUnanswered', {
            is: v => v === 'redirect_internal',
            then: () => yup.object({ display: yup.string().required() })
        }),
        voicemailPin: voicemailPinRequired
            ? voicemailPinSchema.required('Voicemail Pin is required')
            : voicemailPinSchema
    })
}
